import styles from "./index.module.scss";
import classes from "classnames";
import { useEffect, useMemo, useState } from "react";
import MembershipBenefits from "../membership-benefits";
import { getOrderStatus, submitVipOrder } from "../../../../api";
import { QRCodeSVG } from "qrcode.react";
import { PurchaseStatus } from "../../../../types";
import { Loading } from "@quark/design";

export interface MembershipTabsProps {
  hasCourse: boolean;
  loading?: boolean;
  defaultTab?: "one" | "forever";
}

const usePurchaseInfo = (vipCode: string) => {
  const [purchaseInfo, setPurchaseInfo] = useState<Record<string, any>>();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!vipCode) {
      return;
    }
    setLoading(true);
    submitVipOrder(vipCode)
      .then(setPurchaseInfo)
      .finally(() => {
        setLoading(false);
      });
  }, [vipCode]);

  return { purchaseInfo, loading };
};

const usePurchaseStatus = (orderUUID: string): PurchaseStatus => {
  const [status, setStatus] = useState<PurchaseStatus>("SUBMIT");
  const [control, setControl] = useState<AbortController>();

  useEffect(() => {
    if (!orderUUID) {
      return;
    }
    const newControl = new AbortController();
    setControl(newControl);
    return () => {
      newControl.abort("订单已切换");
    };
  }, [orderUUID]);

  useEffect(() => {
    if (!control || !orderUUID) {
      return;
    }
    getOrderStatus(orderUUID, control).then((status) => {
      setStatus(status);
    });
  }, [control, orderUUID]);

  return status;
};

const MembershipTabs = ({
  hasCourse,
  loading,
  defaultTab = "forever",
}: MembershipTabsProps) => {
  const [activeTab, setActiveTab] = useState<"one" | "forever">(
    () => defaultTab
  );
  const vipCode = useMemo<string>(() => {
    if (activeTab === "forever") {
      return "002";
    }
    if (hasCourse) {
      return "003";
    }
    return "001";
  }, [hasCourse, activeTab]);

  const { purchaseInfo, loading: purchaseInfoLoading } =
    usePurchaseInfo(vipCode);
  const purchaseStatus = usePurchaseStatus(purchaseInfo?.orderUUID || "");

  const benefits = useMemo<string[]>(() => {
    if (activeTab === "one") {
      return [
        "买1年送1年，超值特惠",
        "畅学22+精品课程",
        "10年BAT工程师1 v 1 答疑",
        "350+超值题库等你来战",
        "新课上架自动开通",
        "ChatGPT4.0 专享AI编程助手",
        "多端学习，随时随地",
        "会员标识，课程证书",
      ];
    }
    return [
      "开通全站，终身学习",
      "畅享超过22+门顶尖课程",
      "与BAT资深工程师1对1答疑",
      "拥有350+超值题库等你挑战",
      "新课程自动开开通",
      "独享ChatGPT4.0",
      "多平台学习，随时随地畅学",
      "特有会员标识，颁发课程证书",
    ];
  }, [activeTab]);

  useEffect(() => {
    if (purchaseStatus === "FINISHED") {
      window._hmt.push(["_trackEvent", "course", "buy_vip_succeed"]);
    }
  }, [purchaseStatus]);

  const renderPurchaseInfo = () => {
    if (purchaseStatus === "FINISHED") {
      return null;
    }
    return (
      <>
        {purchaseInfo?.codeURL && (
          <QRCodeSVG className={styles.qrcode} value={purchaseInfo?.codeURL} />
        )}
        {purchaseInfo?.price && (
          <div className={styles.price}>
            {purchaseInfoLoading ? "..." : purchaseInfo.price}
          </div>
        )}
      </>
    );
  };

  const handleSwitchTab = (key: "one" | "forever") => {
    window._hmt.push(["_trackEvent", "vip", "switch_vip_modal_tab"]);
    setActiveTab(key);
  };

  return (
    <div className={styles.membershipTabs}>
      {loading && (
        <div className={styles.loading}>
          <Loading />
          <p>请稍后</p>
        </div>
      )}
      <div className={styles.header}>
        <div
          className={classes(styles.tab, activeTab === "one" && styles.active)}
          onClick={() => handleSwitchTab("one")}
        >
          <div className={styles.title}>年费VIP</div>
          <div className={styles.subtitle}>超值优惠 约1元1天</div>
        </div>
        <div
          className={classes(
            styles.center,
            activeTab === "one" && styles.rotate
          )}
        />
        <div
          className={classes(
            styles.tab,
            activeTab === "forever" && styles.active
          )}
          onClick={() => handleSwitchTab("forever")}
        >
          <div className={styles.title}>终身VIP</div>
          <div className={styles.subtitle}>全年最低 终身享用</div>
        </div>
      </div>
      <div className={styles.bar}>
        <span className={styles.title}>开学季限时优惠：</span>
        <span className={styles.content}>
          {activeTab === "one"
            ? "买1年送1年，畅学22+精品课程，1 v 1 答疑，赠送超值题库"
            : "终身VIP 9月特惠，畅学全站，1 v 1 答疑，赠送超值题库"}
        </span>
      </div>
      <div className={styles.body}>
        <div className={styles.benefits}>
          <MembershipBenefits
            benefits={benefits}
            name={activeTab === "one" ? "年费VIP" : "终身VIP"}
          />
        </div>
        <div className={styles.main}>
          <div className={styles.cards}>
            <img
              src="https://python2fun.oss-cn-beijing.aliyuncs.com/vip/267aa98b5ad3c87c9dcd41100.png"
              alt=""
            />
            <img
              src="https://python2fun.oss-cn-beijing.aliyuncs.com/vip/267aa98b5ad3c87c9dcd41101.png"
              alt=""
            />
            <img
              src="https://python2fun.oss-cn-beijing.aliyuncs.com/vip/267aa98b5ad3c87c9dcd41102.png"
              alt=""
            />
            <img
              src="https://python2fun.oss-cn-beijing.aliyuncs.com/vip/267aa98b5ad3c87c9dcd41103.png"
              alt=""
            />
          </div>
          <div
            className={classes(
              styles.purchaseInfo,
              purchaseStatus === "FINISHED" && styles.finished
            )}
          >
            {renderPurchaseInfo()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipTabs;
